import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { BookOpen, Mail, Lock, User, Eye, EyeOff, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { LogoIcon } from '@/components/Logo';
import { LanguageSelector } from '@/components/LanguageSelector';

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [resending, setResending] = useState(false);
  const { googleLogin, apiCall } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters with uppercase, lowercase, and a number'); return; }
    setLoading(true);
    try {
      const API_URL = '';
      const res = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Signup failed');
      setSignupComplete(true);
      toast.success('Account created! Check your email to verify.');
    } catch (err) {
      toast.error(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const API_URL = '';
      await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      toast.success('Verification email sent! Check your inbox.');
    } catch {
      toast.error('Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // Post-signup verification screen
  if (signupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-gradient-to-br from-background via-background to-muted/30" data-testid="verify-prompt-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="verse-card p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-green-500" strokeWidth={1.5} />
            </div>
            <h2 className="font-['Playfair_Display'] text-2xl font-medium mb-3">Verify Your Email</h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-2">
              We've sent a verification link to
            </p>
            <p className="text-foreground font-medium text-sm mb-6" data-testid="signup-email-display">{email}</p>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              {t('common.verify_email_click')}
            </p>

            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full bg-primary text-primary-foreground rounded-full py-3 font-medium hover:opacity-90 transition-opacity text-center"
                data-testid="go-to-login-btn"
              >
                {t('common.go_to_sign_in')}
              </Link>
              <button
                onClick={handleResend}
                disabled={resending}
                className="flex items-center justify-center gap-2 w-full border border-border rounded-full py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors disabled:opacity-50"
                data-testid="resend-verification-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                {resending ? t('common.sending') : t('common.resend_verification')}
              </button>
            </div>

            <p className="text-xs text-muted-foreground mt-6">
              {t('common.check_spam')}
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-gradient-to-br from-background via-background to-muted/30" data-testid="signup-page">
      <Helmet>
        <title>Begin Your Journey — Living Geeta | Bhagavad Gita App</title>
        <meta name="description" content="Create your Living Geeta account. Take the Guna assessment, discover your inner nature, and start your Bhagavad Gita-powered transformation." />
        <link rel="canonical" href="https://livinggeeta.com/signup" />
      </Helmet>
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <LogoIcon className="w-10 h-10" />
            <span className="font-['Playfair_Display'] text-2xl font-semibold">Living Geeta</span>
          </Link>
          <LanguageSelector variant="landing" />
        </div>

        <div className="verse-card p-8">
          <h2 className="font-['Playfair_Display'] text-2xl font-medium text-center mb-2">{t('common.create_account')}</h2>
          <p className="text-muted-foreground text-center text-sm mb-8">{t('common.sign_in_to_continue')}</p>

          <form onSubmit={handleSignup} className="space-y-4">
            <div className="relative">
              <User className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('common.full_name')} required
                className="w-full bg-white/50 border border-primary/10 focus:border-primary/30 rounded-xl pl-10 pr-4 py-3 text-sm outline-none transition-colors"
                data-testid="signup-name-input" />
            </div>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('common.email')} required
                className="w-full bg-white/50 border border-primary/10 focus:border-primary/30 rounded-xl pl-10 pr-4 py-3 text-sm outline-none transition-colors"
                data-testid="signup-email-input" />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('common.password_placeholder')} required
                className="w-full bg-white/50 border border-primary/10 focus:border-primary/30 rounded-xl pl-10 pr-10 py-3 text-sm outline-none transition-colors"
                data-testid="signup-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3.5 text-muted-foreground hover:text-foreground">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-primary text-primary-foreground rounded-full py-3 font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="signup-submit-btn">
              {loading ? '...' : t('common.signup')}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border/50" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-muted-foreground">{t('common.or')}</span></div>
          </div>

          <button onClick={googleLogin}
            className="w-full border border-border rounded-full py-3 font-medium hover:bg-muted/50 transition-colors flex items-center justify-center gap-2 text-sm"
            data-testid="google-signup-btn">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            {t('common.google_login')}
          </button>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {t('common.already_have_account')}{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="login-redirect-link">{t('common.login')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
